import React from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Container, Typography, Grid, Box } from "@mui/material";
import { Element } from "react-scroll";
import guaranteeGraphic from "./guarantee.png";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **PURCHASE-FUNNEL SURFACE.** The refund-promise block on the doors-open
 * /buy landing page; `BuyNow/Top`'s "What's this?" link scrolls to the
 * `guarantee-section` anchor rendered here, so the `<Element name>` is
 * load-bearing and unchanged. Styling-only conversion; the refund copy is
 * byte-identical.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  padding: theme.spacing(6, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(8, 0),
  },
}));

// was: useStyles().guaranteeGraphic — a bare <img>.
// `borderRadius: 10` written as "10px": sx's `borderRadius` is shape-scaled
// (guide §2), so the unit is spelled out to keep the value unambiguous.
const GuaranteeGraphic = styled("img")(({ theme }) => ({
  width: "80px",
  borderRadius: "10px",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    width: "130px",
    marginBottom: theme.spacing(1),
  },
}));

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2rem",
  lineHeight: "2.2rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.4rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "5.4rem",
    lineHeight: "4.8rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme: Theme) => ({
  padding: "16px 0px",
  backgroundColor: "background.ffNavyLighter",
  fontSize: "1.1rem",
  lineHeight: "1.3rem",
  fontWeight: 500,
  letterSpacing: "-1px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.3rem",
    lineHeight: "1.5rem",
  },
});

const Guarantee = () => {
  return (
    <>
      <Element name="guarantee-section">
        <Background>
          <Container maxWidth="md" fixed>
            {/* v5+ Grid: `item` is gone and the breakpoint props collapse into
                `size`. No `spacing` on this container, so widths are unchanged. */}
            <Grid container>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box style={{ display: "flex" }}>
                  {/* v9 removed Box's shorthand system props — `marginRight={2}`
                      would be forwarded to the DOM and apply nothing (guide §2,
                      "Box system props"). Rewritten as `sx`; the value keeps its
                      meaning (spacing multiple ⇒ 16px). */}
                  <Box sx={{ marginRight: 2 }}>
                    <GuaranteeGraphic
                      src={guaranteeGraphic}
                      alt="FarmFLiX App Icon"
                    />
                  </Box>
                  <Box>
                    <Typography variant="h2" sx={titleSx}>
                      The
                    </Typography>
                    <Typography variant="h2" sx={titleLargeSx}>
                      FarmFLiX Guarantee
                    </Typography>
                    <Typography variant="body1" sx={bodyTextSx}>
                      If you don&apos;t love your FarmFLiX Membership in the
                      first month we&apos;ll refund your order, no questions
                      asked. Just contact us to cancel before the renewal date
                      in the following month.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Background>
      </Element>
    </>
  );
};

export default Guarantee;
